(function() {
  'use strict';

  var MIN_CHARS = 2;
  var PAGE_LIMIT = 250;
  var MAX_PAGES = 48;
  var catalogCache = {};
  var catalogLoading = {};
  var skuIndexCache = {};
  var catalogFullyLoaded = {};

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) return window.Shopify.formatMoney(cents);
    return '$' + (Number(cents) / 100).toFixed(2);
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function normalizeQuery(q) {
    return String(q || '').trim().toLowerCase();
  }

  // "B228503Y OR 228503Y OR 228503" -> clean usable terms (Shopify expands with OR)
  function splitSearchTerms(query) {
    return String(query || '')
      .split(/\s+OR\s+|\s+or\s+|\s+/i)
      .map(function(part) { return String(part || '').trim(); })
      .filter(function(part) {
        if (!part) return false;
        var lower = part.toLowerCase();
        return lower !== 'or' && lower !== 'and';
      });
  }

  // Prefer the strongest SKU-like token. Multi-word names stay intact.
  function primaryQuery(query) {
    var raw = String(query || '').trim();
    if (!raw) return '';

    // "pink diamond" / product names → keep FULL phrase (never pick only "diamond")
    if (/\s/.test(raw) && !/\s+OR\s+/i.test(raw)) {
      var nameWords = raw.split(/\s+/).filter(Boolean);
      if (nameWords.length > 1 && !nameWords.some(function(w) { return /\d/.test(w); })) {
        return normalizeQuery(raw);
      }
    }

    var terms = splitSearchTerms(raw);
    if (!terms.length) return normalizeQuery(raw);
    var best = terms[0];
    var bestScore = -1;
    terms.forEach(function(term) {
      var t = term.toLowerCase();
      var score = 0;
      if (/\d/.test(t)) score += 10;
      if (/^[a-z]?\d/i.test(t)) score += 5;
      score += Math.min(t.length, 20);
      if (score > bestScore) {
        bestScore = score;
        best = term;
      }
    });
    return normalizeQuery(best);
  }

  // B217904Y -> 217904y (letter prefix optional in search)
  function skuCore(sku) {
    var s = String(sku || '').trim().toLowerCase();
    if (/^[a-z]\d/i.test(s)) return s.slice(1);
    return s;
  }

  // B228503Y / 228503y / 228503 -> "228503"
  function digitCore(value) {
    var match = String(value || '').toLowerCase().match(/\d{3,}/);
    return match ? match[0] : '';
  }

  function expandSkuQueries(query) {
    var primary = isSkuLikeQuery(query) ? primaryQuery(query) : normalizeQuery(query);
    var q = String(primary || '').trim();
    if (!q) q = String(query || '').trim();
    var list = [q];
    var seen = {};
    seen[q.toLowerCase()] = true;

    function add(term) {
      var key = String(term || '').toLowerCase();
      if (!term || seen[key]) return;
      seen[key] = true;
      list.push(term);
    }

    if (!isSkuLikeQuery(q)) return list;

    var lower = q.toLowerCase();
    if (/^\d/.test(q)) {
      add('B' + q);
      add('b' + q);
    }
    if (/^[a-z]\d/i.test(q)) {
      add(q.slice(1));
    }
    var core = skuCore(lower);
    if (core && core !== lower) {
      add(core);
      add('B' + core);
    }
    var digits = digitCore(lower);
    if (digits && digits !== lower && digits !== core) {
      add(digits);
      add('B' + digits);
      add('b' + digits);
    }
    return list;
  }

  function shopifySkuSearchQuery(query) {
    var parts = expandSkuQueries(query);
    if (parts.length === 1) return parts[0];
    return parts.join(' OR ');
  }

  function productSkus(product) {
    return (product.variants || [])
      .map(function(v) { return String(v.sku || '').trim().toLowerCase(); })
      .filter(Boolean);
  }

  function productHaystack(product) {
    var skus = productSkus(product);
    var parts = [
      product.title || '',
      product.vendor || '',
      product.product_type || '',
      product.handle || '',
      (product.tags || []).join(' '),
      skus.join(' '),
      skus.map(skuCore).join(' ')
    ];
    return parts.join(' ').toLowerCase();
  }

  function skuTermMatches(sku, term) {
    var q = normalizeQuery(term);
    if (q.length < MIN_CHARS) return false;
    var qCore = skuCore(q);
    var qDigits = digitCore(q);
    var core = skuCore(sku);
    var digits = digitCore(sku);
    if (sku === q || core === q || core === qCore) return true;
    if (sku.indexOf(q) !== -1 || core.indexOf(q) !== -1) return true;
    if (qCore && (sku.indexOf(qCore) !== -1 || core.indexOf(qCore) !== -1)) return true;
    if (qDigits && digits && (digits === qDigits || digits.indexOf(qDigits) !== -1 || qDigits.indexOf(digits) !== -1)) return true;
    return false;
  }

  function matchesSkuPartial(product, query) {
    var terms = splitSearchTerms(query);
    if (!terms.length) {
      var single = primaryQuery(query);
      if (single.length < MIN_CHARS) return false;
      terms = [single];
    }
    return productSkus(product).some(function(sku) {
      return terms.some(function(term) {
        return skuTermMatches(sku, term);
      });
    });
  }

  function matchesQuery(product, query) {
    if (matchesSkuPartial(product, query)) return true;
    var words = splitSearchTerms(query).map(normalizeQuery).filter(function(word) {
      return word.length >= MIN_CHARS;
    });
    if (!words.length) return false;
    var hay = productHaystack(product);
    // Name / multi-word: every meaningful word should appear (related products)
    return words.every(function(word) {
      return hay.indexOf(word) !== -1;
    });
  }

  function fetchCollectionPage(handle, page) {
    return fetch(root() + 'collections/' + encodeURIComponent(handle) + '/products.json?limit=' + PAGE_LIMIT + '&page=' + page, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function(res) {
      if (!res.ok) throw new Error('fetch failed');
      return res.json();
    });
  }

  function buildSkuIndex(products) {
    var index = [];
    (products || []).forEach(function(product) {
      (product.variants || []).forEach(function(variant) {
        var sku = String(variant.sku || '').trim().toLowerCase();
        if (!sku) return;
        index.push({ sku: sku, product: product });
      });
    });
    return index;
  }

  function appendCatalogProducts(handle, products) {
    if (!products.length) return catalogCache[handle] || [];
    var existing = catalogCache[handle] || [];
    var seen = {};
    existing.forEach(function(product) {
      seen[String(product.id || '')] = true;
    });
    products.forEach(function(product) {
      var id = String(product.id || '');
      if (!id || seen[id]) return;
      seen[id] = true;
      existing.push(product);
    });
    catalogCache[handle] = existing;
    skuIndexCache[handle] = buildSkuIndex(existing);
    return existing;
  }

  function loadRemainingPages(handle, page, acc) {
    return fetchCollectionPage(handle, page)
      .then(function(data) {
        var products = data.products || [];
        appendCatalogProducts(handle, products);
        if (!products.length || products.length < PAGE_LIMIT || page >= MAX_PAGES) {
          catalogFullyLoaded[handle] = true;
          return catalogCache[handle] || acc;
        }
        return loadRemainingPages(handle, page + 1, catalogCache[handle] || acc);
      })
      .catch(function() {
        catalogFullyLoaded[handle] = true;
        return catalogCache[handle] || acc;
      });
  }

  function loadCatalog(handle) {
    handle = resolveCatalogHandle(handle);
    if (catalogFullyLoaded[handle] && catalogCache[handle]) {
      return Promise.resolve(catalogCache[handle]);
    }
    if (catalogLoading[handle]) return catalogLoading[handle];

    catalogLoading[handle] = fetchCollectionPage(handle, 1)
      .then(function(data) {
        var products = data.products || [];
        appendCatalogProducts(handle, products);
        if (products.length >= PAGE_LIMIT && pageNeedsMore(products)) {
          loadRemainingPages(handle, 2, catalogCache[handle]).catch(function() {});
        } else {
          catalogFullyLoaded[handle] = true;
        }
        delete catalogLoading[handle];
        return catalogCache[handle] || [];
      })
      .catch(function() {
        delete catalogLoading[handle];
        catalogCache[handle] = catalogCache[handle] || [];
        catalogFullyLoaded[handle] = true;
        return catalogCache[handle];
      });

    return catalogLoading[handle];
  }

  function pageNeedsMore(products) {
    return products.length >= PAGE_LIMIT;
  }

  function resolveCatalogHandle(preferred) {
    var handle = String(preferred || '').trim();
    if (handle) return handle;
    return 'all-jewelry';
  }

  function fetchShopifySearchProducts(query, limit) {
    var apiQuery = searchQueryForShopify(query);
    var terms = isSkuLikeQuery(query) ? expandSkuQueries(query) : [apiQuery];
    var fetches = terms.map(function(term) {
      var params = new URLSearchParams();
      params.set('q', term);
      params.set('type', 'product');
      params.set('resources[type]', 'product');
      params.set('resources[limit]', String(Math.max(limit || 8, 10)));

      return fetch(root() + 'search/suggest.json?' + params.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then(function(res) {
          if (!res.ok) throw new Error('search failed');
          return res.json();
        })
        .then(function(data) {
          var products = (((data || {}).resources || {}).results || {}).products || [];
          return products.map(normalizeSuggestProduct);
        })
        .catch(function() {
          return [];
        });
    });

    return Promise.all(fetches).then(function(lists) {
      var merged = [];
      lists.forEach(function(list) {
        merged = mergeProductResults(merged, list, 0);
      });
      // Keep only products related to the FULL typed query (e.g. pink + diamond)
      return filterAndRankProducts(merged, query, limit || 8);
    });
  }

  function runIndexedSkuSearch(index, query, limit) {
    var terms = splitSearchTerms(query);
    if (!terms.length) terms = [primaryQuery(query)];
    var results = [];
    var seen = {};
    for (var i = 0; i < index.length; i++) {
      var sku = index[i].sku;
      var matched = terms.some(function(term) {
        return skuTermMatches(sku, term);
      });
      if (!matched) continue;
      var id = String(index[i].product.id || '');
      if (!id || seen[id]) continue;
      seen[id] = true;
      results.push(index[i].product);
      if (limit && results.length >= limit) break;
    }
    return results;
  }

  function runCatalogSearch(catalog, query, limit) {
    var q = normalizeQuery(query);
    var matched = [];
    var seen = {};

    (catalog || []).forEach(function(product) {
      var id = String(product.id || '');
      if (!id || seen[id]) return;
      if (matchesSkuPartial(product, q) || matchesQuery(product, q)) {
        seen[id] = true;
        matched.push(product);
      }
    });

    matched.sort(function(a, b) {
      return scoreProductMatch(b, q) - scoreProductMatch(a, q);
    });

    if (limit && limit > 0) return matched.slice(0, limit);
    return matched;
  }

  function normalizeSuggestProduct(product) {
    var handle = product.handle || '';
    if (!handle && product.url) {
      var match = String(product.url).match(/\/products\/([^/?#]+)/);
      if (match) handle = match[1];
    }

    var imageSrc = '';
    if (product.featured_image && product.featured_image.url) {
      imageSrc = product.featured_image.url;
    } else if (product.image) {
      imageSrc = product.image;
    }

    var price = parseFloat(product.price_min || product.price || 0);
    if (Number.isNaN(price)) price = 0;

    return {
      id: product.id,
      title: product.title || '',
      handle: handle,
      images: imageSrc ? [{ src: imageSrc }] : [],
      variants: [{ price: price, sku: '' }]
    };
  }

  function mergeProductResults(primary, secondary, limit) {
    var merged = [];
    var seen = {};

    function pushList(list) {
      (list || []).forEach(function(product) {
        var id = String(product.id || '');
        var handle = String(product.handle || '');
        var key = id || handle;
        if (!key || seen[key]) return;
        seen[key] = true;
        merged.push(product);
      });
    }

    pushList(primary);
    pushList(secondary);

    if (limit && limit > 0) return merged.slice(0, limit);
    return merged;
  }

  function searchLocalCatalog(handle, query, limit) {
    var index = skuIndexCache[handle];
    if (index && index.length) {
      var indexed = runIndexedSkuSearch(index, query, limit);
      if (indexed.length) return indexed;
    }

    var catalog = catalogCache[handle];
    if (catalog && catalog.length) {
      return runCatalogSearch(catalog, query, limit);
    }

    return [];
  }

  function isSkuLikeQuery(query) {
    var raw = String(query || '').trim();
    if (!raw) return false;
    // Multi-word names ("pink diamond") are product searches, not SKUs
    if (/\s/.test(raw) && !/\s+OR\s+/i.test(raw)) {
      var words = raw.split(/\s+/).filter(Boolean);
      if (words.length > 1) return false;
    }
    var q = primaryQuery(raw);
    return /\d/.test(q);
  }

  function searchQueryForShopify(query) {
    if (isSkuLikeQuery(query)) return primaryQuery(query);
    // Keep full phrase for name search (pink diamond → pink diamond)
    return String(query || '').trim();
  }

  function scoreProductMatch(product, query) {
    var q = normalizeQuery(query);
    if (!q) return 0;
    var title = normalizeQuery(product.title || '');
    var hay = productHaystack(product);
    var score = 0;
    if (title === q) score += 100;
    if (title.indexOf(q) === 0) score += 80;
    if (title.indexOf(q) !== -1) score += 50;
    if (hay.indexOf(q) !== -1) score += 30;
    var words = q.split(/\s+/).filter(function(w) { return w.length >= MIN_CHARS; });
    if (words.length) {
      var hit = 0;
      words.forEach(function(w) {
        if (hay.indexOf(w) !== -1) hit++;
      });
      score += Math.round((hit / words.length) * 40);
    }
    if (matchesSkuPartial(product, query)) score += 20;
    return score;
  }

  function filterAndRankProducts(products, query, limit) {
    var q = normalizeQuery(query);
    var list = (products || []).filter(function(product) {
      if (isSkuLikeQuery(query)) return matchesSkuPartial(product, query) || matchesQuery(product, query);
      return matchesQuery(product, query);
    });
    list.sort(function(a, b) {
      return scoreProductMatch(b, q) - scoreProductMatch(a, q);
    });
    if (limit && limit > 0) return list.slice(0, limit);
    return list;
  }

  // Autocomplete: product titles that start with / contain what the user typed
  function buildTitleSuggestions(products, query, limit) {
    var q = normalizeQuery(query);
    if (q.length < MIN_CHARS) return [];
    var starts = [];
    var contains = [];
    var seen = {};
    (products || []).forEach(function(product) {
      var title = String(product.title || '').trim();
      if (!title) return;
      var lower = title.toLowerCase();
      if (seen[lower]) return;
      if (lower.indexOf(q) === -1) return;
      seen[lower] = true;
      if (lower.indexOf(q) === 0) starts.push(title);
      else contains.push(title);
    });
    var out = starts.concat(contains);
    if (limit && limit > 0) return out.slice(0, limit);
    return out;
  }

  function searchSkuOnly(query, options) {
    options = options || {};
    var handle = resolveCatalogHandle(options.collectionHandle);
    var limit = options.limit || 8;
    var q = searchQueryForShopify(query);
    if (q.length < MIN_CHARS) return Promise.resolve([]);

    var cachedIndex = skuIndexCache[handle];
    if (cachedIndex && cachedIndex.length) {
      var cachedMatches = runIndexedSkuSearch(cachedIndex, query, limit);
      if (cachedMatches.length) return Promise.resolve(cachedMatches);
    }

    var cachedCatalog = catalogCache[handle];
    if (cachedCatalog && cachedCatalog.length) {
      var catalogMatches = cachedCatalog.filter(function(product) {
        return matchesSkuPartial(product, query);
      });
      if (catalogMatches.length) {
        return Promise.resolve(catalogMatches.slice(0, limit));
      }
    }

    // Instant: Shopify suggest first. Catalog page only if still empty.
    return fetchShopifySearchProducts(q, limit).then(function(shopifyHits) {
      if (shopifyHits.length) return shopifyHits.slice(0, limit);
      return loadCatalog(handle).then(function(catalog) {
        var index = skuIndexCache[handle] || buildSkuIndex(catalog);
        var indexed = runIndexedSkuSearch(index, query, limit);
        if (!indexed.length) {
          indexed = (catalog || []).filter(function(product) {
            return matchesSkuPartial(product, query);
          }).slice(0, limit);
        }
        return indexed;
      });
    });
  }

  function loadCatalogFully(handle) {
    handle = resolveCatalogHandle(handle);
    if (catalogFullyLoaded[handle] && catalogCache[handle]) {
      return Promise.resolve(catalogCache[handle]);
    }
    return loadCatalog(handle).then(function(firstPage) {
      if (catalogFullyLoaded[handle]) return catalogCache[handle] || firstPage || [];
      return loadRemainingPages(handle, 2, catalogCache[handle] || firstPage || []);
    });
  }

  function searchCatalog(query, options) {
    options = options || {};
    var handle = resolveCatalogHandle(options.collectionHandle);
    var limit = options.limit;
    if (limit == null) limit = 8;
    if (normalizeQuery(query).length < MIN_CHARS) return Promise.resolve([]);

    var q = searchQueryForShopify(query);
    var localResults = searchLocalCatalog(handle, query, limit);
    var suggestLimit = limit > 0 ? Math.min(limit, 10) : 10;
    var suggestPromise = fetchShopifySearchProducts(q, suggestLimit);

    // Fast path: local cache + Shopify suggest — never block on full catalog crawl
    if (limit > 0 && localResults.length >= limit) {
      return suggestPromise.then(function(suggestProducts) {
        return mergeProductResults(localResults, suggestProducts, limit);
      });
    }

    var catalogPromise = catalogCache[handle]
      ? Promise.resolve(catalogCache[handle])
      : loadCatalog(handle);

    return Promise.all([catalogPromise, suggestPromise]).then(function(results) {
      var catalog = results[0] || [];
      var suggestProducts = results[1] || [];
      var catalogMatches = runCatalogSearch(catalog, query, limit);
      if (!catalogMatches.length && localResults.length) catalogMatches = localResults;
      return mergeProductResults(suggestProducts, catalogMatches, limit);
    });
  }

  // Instant results for search page / suggestions (Shopify suggest + cache)
  function searchFast(query, options) {
    options = options || {};
    var handle = resolveCatalogHandle(options.collectionHandle);
    var limit = options.limit == null ? 24 : options.limit;
    if (normalizeQuery(query).length < MIN_CHARS) return Promise.resolve([]);

    var local = searchLocalCatalog(handle, query, limit);
    return fetchShopifySearchProducts(query, Math.min(limit || 10, 10)).then(function(suggestProducts) {
      return filterAndRankProducts(mergeProductResults(suggestProducts, local, 0), query, limit);
    });
  }

  // Dropdown: related products + title autocomplete for what user typed
  function searchWithSuggestions(query, options) {
    options = options || {};
    var handle = resolveCatalogHandle(options.collectionHandle);
    var limit = options.limit == null ? 8 : options.limit;
    if (normalizeQuery(query).length < MIN_CHARS) {
      return Promise.resolve({ products: [], suggestions: [] });
    }

    return searchFast(query, { collectionHandle: handle, limit: limit }).then(function(products) {
      var pool = products.slice();
      var cached = catalogCache[handle] || [];
      if (cached.length) {
        pool = mergeProductResults(pool, cached, 0);
      }
      var suggestions = buildTitleSuggestions(pool, query, 5);
      // Prefer exact phrase completions that keep typed prefix
      return {
        products: filterAndRankProducts(products, query, limit),
        suggestions: suggestions
      };
    });
  }

  // Optional extra matches from first catalog page only (no 48-page crawl)
  function searchMore(query, options) {
    options = options || {};
    var handle = resolveCatalogHandle(options.collectionHandle);
    if (normalizeQuery(query).length < MIN_CHARS) return Promise.resolve([]);
    return loadCatalog(handle).then(function(catalog) {
      return filterAndRankProducts(runCatalogSearch(catalog || [], query, 0), query, 0);
    });
  }

  function searchProducts(query, options) {
    options = options || {};
    if (options.limit == null) options.limit = 8;
    return searchFast(query, options);
  }

  function searchAllProducts(query, options) {
    options = options || {};
    var handle = resolveCatalogHandle(options.collectionHandle);
    return searchFast(query, { collectionHandle: handle, limit: 24 }).then(function(fast) {
      return searchMore(query, { collectionHandle: handle }).then(function(more) {
        return mergeProductResults(fast, more, 0);
      });
    });
  }

  function getPrimarySku(product, query) {
    var variants = product.variants || [];
    var terms = splitSearchTerms(query);
    if (!terms.length) terms = [primaryQuery(query)];
    if (terms.length) {
      for (var i = 0; i < variants.length; i++) {
        var sku = String(variants[i].sku || '').trim();
        if (!sku) continue;
        var skuLower = sku.toLowerCase();
        if (terms.some(function(term) { return skuTermMatches(skuLower, term); })) {
          return sku;
        }
      }
    }
    for (var j = 0; j < variants.length; j++) {
      if (variants[j].sku) return variants[j].sku;
    }
    return '';
  }

  function buildSpsProductItems(products, query) {
    if (!products.length) return '';
    var html = '<p class="sps-heading">Products</p><ul class="sps-list" role="listbox">';
    products.forEach(function(product) {
      var img = product.images && product.images[0] ? product.images[0].src : '';
      var variant = product.variants && product.variants[0] ? product.variants[0] : null;
      var sku = getPrimarySku(product, query);
      var priceCents = variant ? Math.round(Number(variant.price) * 100) : 0;
      var url = root() + 'products/' + product.handle;

      html += '<li role="option"><a href="' + url + '" class="sps-item">';
      if (img) {
        html += '<img src="' + escapeHtml(img) + '" alt="" class="sps-thumb" width="48" height="48" loading="lazy">';
      } else {
        html += '<span class="sps-thumb sps-thumb--empty" aria-hidden="true"></span>';
      }
      html += '<span class="sps-meta">';
      html += '<span class="sps-title">' + escapeHtml(product.title) + '</span>';
      if (sku) html += '<span class="sps-sku">SKU: ' + escapeHtml(sku) + '</span>';
      html += '<span class="sps-price">' + formatMoney(priceCents) + '</span>';
      html += '</span></a></li>';
    });
    html += '</ul>';
    return html;
  }

  function buildViewAllLink(query) {
    return (
      '<a href="' + root() + 'search?q=' + encodeURIComponent(query) + '&type=product" class="sps-view-all">' +
      'View all results for &ldquo;' + escapeHtml(query) + '&rdquo;</a>'
    );
  }

  function stripPredictiveProducts(html) {
    if (!html) return '';
    var doc = new DOMParser().parseFromString('<div id="sps-wrap">' + html + '</div>', 'text/html');
    var wrap = doc.getElementById('sps-wrap');
    if (!wrap) return html;

    wrap.querySelectorAll('.sps-heading').forEach(function(heading) {
      if ((heading.textContent || '').trim() !== 'Products') return;
      var next = heading.nextElementSibling;
      heading.remove();
      if (next && next.classList.contains('sps-list')) next.remove();
    });

    wrap.querySelectorAll('.sps-view-all').forEach(function(el) { el.remove(); });
    wrap.querySelectorAll('.sps-no-results').forEach(function(el) { el.remove(); });
    return wrap.innerHTML;
  }

  function buildQuerySuggestionItems(titles, query) {
    if (!titles || !titles.length) return '';
    var html = '<p class="sps-heading">Suggestions</p><ul class="sps-queries" role="listbox">';
    titles.forEach(function(title) {
      var href = root() + 'search?q=' + encodeURIComponent(title) + '&type=product';
      html +=
        '<li role="option"><a href="' +
        href +
        '" class="sps-query-item" data-sps-suggest-fill="' +
        escapeHtml(title) +
        '">' +
        escapeHtml(title) +
        '</a></li>';
    });
    html += '</ul>';
    return html;
  }

  function mergeSpsDropdown(predictiveInnerHtml, skuProducts, query, titleSuggestions) {
    var extra = stripPredictiveProducts(predictiveInnerHtml);
    var suggestHtml = buildQuerySuggestionItems(titleSuggestions || [], query);
    var productsHtml = buildSpsProductItems(skuProducts || [], query);

    if (!productsHtml && !suggestHtml && !extra) {
      return '<div class="sps-dropdown-inner"><p class="sps-no-results">No results found. Try product name or SKU (2+ characters).</p></div>';
    }

    return (
      '<div class="sps-dropdown-inner">' +
      suggestHtml +
      productsHtml +
      extra +
      buildViewAllLink(query) +
      '</div>'
    );
  }

  function buildPredictiveSkuGrid(products, query) {
    if (!products.length) return '';
    var html = '<div class="predictive-search__sku-fallback">';
    html += '<p class="predictive-search__title h5">Products</p>';
    html += '<div class="predictive-search__grid predictive-search__grid--products" data-grid-id="products">';
    products.forEach(function(product, index) {
      var img = product.images && product.images[0] ? product.images[0].src : '';
      var variant = product.variants && product.variants[0] ? product.variants[0] : null;
      var sku = getPrimarySku(product, query);
      var priceCents = variant ? Math.round(Number(variant.price) * 100) : 0;
      var url = root() + 'products/' + product.handle;

      html += '<div class="predictive-search__grid-item predictive-search__sku-item" data-animate data-animate-order="' + (index + 1) + '">';
      html += '<a href="' + url + '" class="predictive-search__sku-link">';
      if (img) {
        html += '<img src="' + escapeHtml(img) + '" alt="" class="predictive-search__sku-image" loading="lazy" width="120" height="120">';
      }
      html += '<div class="predictive-search__sku-meta">';
      html += '<span class="predictive-search__sku-title">' + escapeHtml(product.title) + '</span>';
      if (sku) html += '<span class="predictive-search__sku-code">SKU: ' + escapeHtml(sku) + '</span>';
      html += '<span class="predictive-search__sku-price">' + formatMoney(priceCents) + '</span>';
      html += '</div></a></div>';
    });
    html += '</div></div>';
    return html;
  }

  function mergePredictiveHtml(predictiveHtml, skuProducts, query) {
    if (!skuProducts.length) return predictiveHtml;

    if (!predictiveHtml) {
      return (
        '<div class="predictive-search__results-wrapper">' +
          '<div class="predictive-search__results wrapper-spacing--h">' +
            buildPredictiveSkuGrid(skuProducts, query) +
            '<div class="predictive-search__view-all-mobile text-link-animated">' +
              '<a href="' + root() + 'search?q=' + encodeURIComponent(query) + '&type=product" class="predictive-search__list-item">View all</a>' +
            '</div>' +
          '</div>' +
        '</div>'
      );
    }

    var doc = new DOMParser().parseFromString(predictiveHtml, 'text/html');
    var wrapper = doc.querySelector('.predictive-search__results-wrapper');
    if (!wrapper) return predictiveHtml + buildPredictiveSkuGrid(skuProducts, query);

    var productsGrid = wrapper.querySelector('[data-grid-id="products"]');
    if (productsGrid) productsGrid.remove();

    var mainResults = wrapper.querySelector('.predictive-search__main-results');
    var results = wrapper.querySelector('.predictive-search__results');
    var skuBlock = buildPredictiveSkuGrid(skuProducts, query);

    if (mainResults) {
      mainResults.insertAdjacentHTML('afterbegin', skuBlock);
    } else if (results) {
      results.insertAdjacentHTML('afterbegin', skuBlock);
    } else {
      wrapper.insertAdjacentHTML('afterbegin', skuBlock);
    }

    var noResults = wrapper.querySelector('.predictive-search__no-results');
    if (noResults) noResults.remove();

    return wrapper.outerHTML;
  }

  function collectCatalogHandles() {
    var handles = {};
    document.querySelectorAll('[data-search-catalog-handle]').forEach(function(el) {
      var handle = String(el.getAttribute('data-search-catalog-handle') || '').trim();
      if (handle) handles[handle] = true;
    });
    if (!Object.keys(handles).length) handles['all-jewelry'] = true;
    return Object.keys(handles);
  }

  function preloadCatalogs() {
    collectCatalogHandles().forEach(function(handle) {
      loadCatalog(handle);
    });
  }

  function preloadCatalog(handle) {
    return loadCatalog(resolveCatalogHandle(handle));
  }

  function bindSkuSearchForms() {
    document.querySelectorAll('form[action*="/search"], form.search-bar, form.search-page-bar__form').forEach(function(form) {
      if (form.dataset.skuPrefixBound === 'true') return;
      form.dataset.skuPrefixBound = 'true';
      form.addEventListener('submit', function(e) {
        var input = form.querySelector('input[name="q"], input[type="search"]');
        if (!input) return;
        var raw = (input.value || '').trim();
        if (raw.length < MIN_CHARS) return;

        // Product names: submit EXACT typed text (pink diamond stays pink diamond)
        if (!isSkuLikeQuery(raw)) {
          input.value = raw;
          return;
        }

        // SKU only: normalize to primary token (no OR spam in URL)
        input.value = primaryQuery(raw) || raw;
      });
    });
  }

  window.SearchSkuSuggestions = {
    MIN_CHARS: MIN_CHARS,
    search: searchProducts,
    searchWithSuggestions: searchWithSuggestions,
    searchSku: searchSkuOnly,
    searchAll: searchAllProducts,
    searchFast: searchFast,
    searchMore: searchMore,
    preload: preloadCatalog,
    primaryQuery: primaryQuery,
    isSkuLikeQuery: isSkuLikeQuery,
    splitSearchTerms: splitSearchTerms,
    expandSkuQueries: expandSkuQueries,
    shopifySkuSearchQuery: shopifySkuSearchQuery,
    buildTitleSuggestions: buildTitleSuggestions,
    buildSpsProductItems: buildSpsProductItems,
    mergeSpsDropdown: mergeSpsDropdown,
    mergePredictiveHtml: mergePredictiveHtml
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      preloadCatalogs();
      bindSkuSearchForms();
    });
  } else {
    preloadCatalogs();
    bindSkuSearchForms();
  }
  document.addEventListener('shopify:section:load', bindSkuSearchForms);
})();
